import axios from 'axios';
import { getApiBaseUrl } from '../utils/config';
import { TelegramAuthData } from '@telegram-auth/react';

const API_BASE_URL = getApiBaseUrl();

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.removeItem('token');
      // 通知 AuthContext 清空登录态
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));

      // Telegram 小程序内不做硬跳转：整页刷新会打断 TelegramAutoLogin 的自动登录并造成闪屏，
      // 清掉 token 后交给 TelegramAutoLogin 重新自动登录即可。
      const inTelegram = Boolean((window as any)?.Telegram?.WebApp?.initData);
      const onLoginPage = window.location.pathname.endsWith('/login');
      if (!inTelegram && !onLoginPage) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const guestAPI = {
  commonConfig: () => api.get('/guest/comm/config'),
  guestPlans: () => api.get('/guest/plan/fetch'),
}

// API endpoints
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/passport/auth/login', { email, password }),
  register: (email: string, password: string, email_code?: string, invite_code?: string, recaptcha_data?: string) => {
    const data: any = { email, password };
    if (email_code) data.email_code = email_code;
    if (invite_code) data.invite_code = invite_code;
    if (recaptcha_data) data.recaptcha_data = recaptcha_data;
    return api.post('/passport/auth/register', data);
  },
  telegramLogin: (telegram_id: number, initData: string) =>
    api.post('/passport/auth/telegram-login', { telegram_id, init_data: initData }),
  telegramWidgetLogin: (telegramUser: TelegramAuthData) =>
    api.post('/passport/auth/telegram-widget-login', telegramUser),
  sendEmailVerify: (email: string) => api.post('/passport/comm/sendEmailVerify', { email }),
  forgetPassword: (email: string, email_code: string, password: string) => api.post('/passport/auth/forget', { email, email_code, password }),


};

export const userAPI = {
  checkLogin: () => api.get('/user/checkLogin'),
  changePassword: (old_password: string, new_password: string) =>
    api.post('/user/changePassword', { old_password, new_password }),
  getUserInfo: () => api.get('/user/info'),
  getStats: () => api.get('/user/getStat'),
  getOrders: () => api.get('/user/order'),
  getSubInfo: () => api.get('/user/getSubscribe'),
  getConfig: () => api.get('/user/comm/config'),
  //到期通知或者流量提醒
  updateRemindSettings: (settings: { remind_expire?: number, remind_traffic?: number }) => api.post('/user/update', settings),
  getBotInfo: () => api.get('/user/telegram/getBotInfo'),
  resetSecurity: () => api.get('/user/resetSecurity'),
};

export const planAPI = {
  getPlans: () => api.get('/user/plan/fetch'),
  getPlanDetail: (id: number) => api.get(`/user/plan/fetch?id=${id}`),
  couponCheck: (code: string, plan_id: number) => api.post('/user/coupon/check', { code: code, plan_id: plan_id }),
};
export const orderAPI = {
  saveOrder: (plan_id: number, period: string, coupon_code?: string) => {
    return api.post('/user/order/save', { plan_id, period, coupon_code })
    // const formData = new FormData();
    // formData.append('plan_id', plan_id.toString());
    // formData.append('period', period);
    // if (coupon_code) {
    //   formData.append('coupon_code', coupon_code);
    // }

    // return api.post('/user/order/save', formData, {
    //   headers: {
    //     'Content-Type': 'application/x-www-form-urlencoded',
    //   },
    // });
  },
  cancelOrder: (trade_no: string) => {
    const formData = new FormData();
    formData.append('trade_no', trade_no);
    return api.post('/user/order/cancel', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  },

  checkOrder: (trade_no: string) =>
    api.get(`/user/order/check?trade_no=${trade_no}`),
  getOrderDetail: (trade_no: string) =>
    api.get(`/user/order/detail?trade_no=${trade_no}`),
  getPaymentMethods: () =>
    api.get('/user/order/getPaymentMethod'),
  checkOut: (trade_no: string, method: number) =>
    api.post('/user/order/checkout', { trade_no, method }),
  getOrders: () =>
    api.get('/user/order/fetch'),
};

export const serverAPI = {
  getServers: () => api.get('/user/server/fetch'),
};

//使用教程
export const knowledgeAPI = {
  getKnowledge: () => api.get('/user/knowledge/fetch?language=zh-CN'),
  getKnowledgeDetail: (id: number) => api.get(`/user/knowledge/fetch?id=${id}&language=zh-CN`),
};
//工单
export const ticketAPI = {
  getTickets: () => api.get('/user/ticket/fetch'),
  getTicketDetail: (id: number) => api.get(`/user/ticket/fetch?id=${id}`),
  createTicket: (data: any) => api.post('/user/ticket/save', data),
  replyTicket: (id: number, message: string) => api.post(`/user/ticket/reply`, { id, message }),
  closeTicket: (id: number) => api.post(`/user/ticket/close`, { id }),
};
//邀请
export const inviteAPI = {
  //邀请详情
  inviteDetails: (current?: Number, pageSize?: Number) => {
    let url = '/user/invite/details';
    const params = [];
    if (current) params.push(`current=${current}`);
    if (pageSize) params.push(`page_size=${pageSize}`);
    if (params.length) url += '?' + params.join('&');
    return api.get(url);
  },
  //邀请码列表及邀请概况
  getInvites: () => api.get('/user/invite/fetch'),
  createInvite: () => api.get('/user/invite/save'),
  //划转到余额
  transferToBalance: (transfer_amount: string) => {
    const formData = new FormData();
    formData.append('transfer_amount', transfer_amount);
    return api.post('/user/transfer', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  },
  //提现
  withdraw: (withdraw_account: string, withdraw_method: string) => {
    const formData = new FormData();
    formData.append('withdraw_account', withdraw_account);
    formData.append('withdraw_method', withdraw_method);
    return api.post('/user/ticket/withdraw', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  },
};
